import React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@one-thd/sui-atomic-components';
import { DeliveryExpress } from '@one-thd/sui-icons';

const PRIORITY_DELIVERY_TYPES = {
  SAME_DAY: 'same-day',
  ON_DEMAND: 'on-demand'
};

export const getPriorityDeliveryGroups = (priorityDeliveryOptions = []) => {
  if (!Array.isArray(priorityDeliveryOptions) || priorityDeliveryOptions.length === 0) {
    return {
      sameDayPriorityOption: null,
      additionalPriorityDeliveryOptions: []
    };
  }

  return {
    sameDayPriorityOption: priorityDeliveryOptions.find((option) => option?.type === PRIORITY_DELIVERY_TYPES.SAME_DAY) || null,
    additionalPriorityDeliveryOptions: priorityDeliveryOptions.filter((option) => option?.type !== PRIORITY_DELIVERY_TYPES.SAME_DAY)
  };
};

const formatDollarAmount = (charge, options = {}) => {
  const {
    showChargeDelta = false,
    hideCents = false
  } = options;

  if (!charge) {
    return '';
  }

  const chargeString = String(charge);
  const parsedCharge = Number(chargeString);
  let formattedCharge = chargeString;

  if (!Number.isNaN(parsedCharge)) {
    if (hideCents) {
      formattedCharge = String(Math.trunc(parsedCharge));
    } else {
      const truncatedCharge = Math.trunc(parsedCharge * 100) / 100;
      formattedCharge = truncatedCharge.toFixed(2);
    }
  }

  return `${showChargeDelta ? '+' : ''}$${formattedCharge}`;
};

const formatTimelineLabel = (timeline) => {
  if (!timeline || timeline.toLowerCase() === 'today') {
    return 'Today';
  }
  return timeline;
};

const formatOnDemandLabel = (timeline) => {
  const optionTimeline = formatTimelineLabel(timeline);
  const hourMatch = optionTimeline.match(/^(\d+)\s+hours?$/i);
  if (hourMatch) {
    return `${hourMatch[1]} Hour Delivery`;
  }
  return `${optionTimeline} Delivery`;
};

const PriorityDeliveryOptionRow = ({ option, showChargeDelta, showDivider }) => {
  const optionLabel = formatOnDemandLabel(option?.timeline);
  const isOnDemandOption = option?.type === PRIORITY_DELIVERY_TYPES.ON_DEMAND;

  return (
    <>
      {showDivider && <hr className="sui-mx-2 sui-my-0" />}
      <div className="sui-flex sui-items-center sui-justify-between sui-px-3 sui-py-2">
        <Typography variant="body-base">
          <span className="sui-inline-flex sui-items-center sui-gap-2">
            <span className="sui-inline-flex sui-shrink-0" data-testid="delivery-express-priority-option-icon">
              <DeliveryExpress size="small" />
            </span>
            <span className="sui-font-bold">{optionLabel}</span>
          </span>
        </Typography>
        <Typography variant="body-base">{formatDollarAmount(option?.totalCharge, { showChargeDelta: showChargeDelta && isOnDemandOption })}</Typography>
      </div>
    </>
  );
};

PriorityDeliveryOptionRow.propTypes = {
  option: PropTypes.shape({
    timeline: PropTypes.string,
    totalCharge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string
  }),
  showChargeDelta: PropTypes.bool,
  showDivider: PropTypes.bool
};

PriorityDeliveryOptionRow.defaultProps = {
  option: null,
  showChargeDelta: false,
  showDivider: true
};

export const PriorityDeliveryMessageCard = ({
  additionalPriorityDeliveryOptions,
  bodfsCutoff,
  bodfsLatestDelivery,
  fsdbmProgramInclusion,
  sameDayPriorityOption
}) => {
  return (
    <div className="sui-pt-4" data-testid="delivery-message-under-tile">
      <div className="sui-rounded-lg sui-border-solid sui-border-1 sui-m-1px sui-border-primary">
        <div className="sui-rounded-t-lg sui-flex sui-items-center sui-justify-between sui-px-3 sui-py-2 sui-bg-subtle">
          <Typography variant="h5" className="sui-font-bold">Get It Faster *</Typography>
          <Typography variant="body-xs">*Schedule in Checkout</Typography>
        </div>
        <hr className="sui-my-0" />

        {sameDayPriorityOption && (
          <div className="sui-flex sui-items-center sui-justify-between sui-px-3 sui-py-2">
            <Typography variant="body-base">
              Get it <span className="sui-font-bold">Today</span> by {bodfsLatestDelivery}
            </Typography>
            <Typography color="primary" variant="body-base" weight="regular">
              {formatDollarAmount(sameDayPriorityOption?.totalCharge)}
            </Typography>
          </div>
        )}

        {fsdbmProgramInclusion && (
          <div className={`sui-px-2 sui-pb-2 ${sameDayPriorityOption ? 'sui-pt-0' : 'sui-pt-2'}`}>
            <div className="sui-rounded-md sui-bg-gradient-to-r sui-from-[#E0F8ED] sui-to-[#9CE8C4] sui-p-2">
              <Typography variant="body-sm">
                <>
                  {sameDayPriorityOption ? 'Or get ' : 'Get '}
                  <span className="sui-font-bold sui-text-success">FREE</span>{' '}
                  <span className="sui-font-bold">Delivery Today</span>{' '}
                  with {formatDollarAmount(fsdbmProgramInclusion?.threshold, { hideCents: true })} of eligible items when you order by {bodfsCutoff}
                </>
              </Typography>
            </div>
          </div>
        )}

        {additionalPriorityDeliveryOptions.map((option, index) => (
          <PriorityDeliveryOptionRow
            key={`${option?.type || 'priority-delivery'}-${index}`}
            option={option}
            showChargeDelta={!sameDayPriorityOption}
            showDivider={!!sameDayPriorityOption || !!fsdbmProgramInclusion || index > 0}
          />
        ))}
      </div>
    </div>
  );
};

PriorityDeliveryMessageCard.propTypes = {
  additionalPriorityDeliveryOptions: PropTypes.arrayOf(PropTypes.object),
  bodfsCutoff: PropTypes.string,
  bodfsLatestDelivery: PropTypes.string,
  fsdbmProgramInclusion: PropTypes.shape({
    description: PropTypes.string,
    threshold: PropTypes.string
  }),
  sameDayPriorityOption: PropTypes.object
};

PriorityDeliveryMessageCard.defaultProps = {
  additionalPriorityDeliveryOptions: [],
  bodfsCutoff: '4pm',
  bodfsLatestDelivery: '8pm',
  fsdbmProgramInclusion: null,
  sameDayPriorityOption: null
};
